import { Axios } from '@helpers/ApiService';
import { NotificationManager } from './NotificationManager';

export interface InternalNotification {
  id: number;
  butcherId: number;
  moduleId: number;
  actionKey: string;
  roleIds: number[];
  message: string;
  isRead: number[];
  createdBy?: number;
  createdAt: string;
  modifiedBy?: number;
  modifiedAt: string;
  deletedBy?: number;
  deletedAt?: string;
  Butcher: {
    id: number;
    companyName?: string;
  };
  module: {
    id: number;
    name: string;
  };
}

export interface NotificationResponse {
  data: InternalNotification[];
  total: number;
  page: number;
  limit: number;
}

export const NotificationService = {
  /**
   * Fetch notifications for specific user roles
   * @param roles - Array of role IDs to fetch notifications for
   * @param page - Page number for pagination (optional)
   * @param count - Number of items per page (optional, defaults to 10)
   * @returns Promise with notifications including isReadByUser flag
   */
  async fetchUserNotifications(
    roles: number[],
    page?: number,
    count?: number,
  ): Promise<{
    success: boolean;
    data?: {
      notifications: InternalNotification[];
      totalCount: number;
      totalPages: number;
      currentPage: number;
      unreadCount: number;
    };
    message: string;
  }> {
    try {
      const requestBody: { roles: number[]; page?: number; count?: number } = {
        roles,
      };

      // Add pagination parameters if provided
      if (page !== undefined) {
        requestBody.page = page;
      }
      if (count !== undefined) {
        requestBody.count = count;
      }

      const response = await Axios.post(
        '/notifications/fetch-user-notifications',
        requestBody,
      );

      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
          ? String((error.response.data as Record<string, unknown>).message)
          : 'Failed to fetch notifications';
      return {
        success: false,
        message: errorMessage,
      };
    }
  },

  /**
   * Fetch all notifications for the current butcher
   * @param page - Page number for pagination
   * @param limit - Number of items per page
   * @param isRead - Filter by read status (optional)
   * @returns Promise<NotificationResponse>
   */
  async getAllNotifications(
    page = 1,
    limit = 20,
    isRead?: boolean,
  ): Promise<NotificationResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (isRead !== undefined) {
      params.append('isRead', isRead.toString());
    }

    const response = await Axios.get(`/notifications?${params.toString()}`);
    return response.data;
  },

  /**
   * Mark multiple notifications as read
   * @param notificationIds - Array of notification IDs to mark as read
   * @returns Promise<{ updatedCount: number; processedIds: number[] }>
   */
  async markNotificationsAsRead(
    notificationIds: number[],
  ): Promise<{ updatedCount: number; processedIds: number[] }> {
    const response = await Axios.post('/notifications/mark-notification-read', {
      notificationIds,
    });

    // Update local storage using centralized NotificationManager
    if (response.data.success) {
      NotificationManager.markNotificationsAsRead(notificationIds);
    }

    return response.data.data;
  },

  /**
   * Mark all notifications as read for the current user
   * @param allNotificationIds - Array of all notification IDs to mark as read
   * @returns Promise<{ updatedCount: number; processedIds: number[] }>
   */
  async markAllAsRead(
    allNotificationIds: number[],
  ): Promise<{ updatedCount: number; processedIds: number[] }> {
    return this.markNotificationsAsRead(allNotificationIds);
  },

  /**
   * Get unread notification count
   * @returns Promise<number>
   */
  async getUnreadCount(): Promise<number> {
    const response = await Axios.get('/notifications/unread-count');
    return response.data.count;
  },

  /**
   * Delete a notification
   * @param notificationId - ID of the notification to delete
   * @returns Promise<void>
   */
  async deleteNotification(notificationId: number): Promise<void> {
    await Axios.delete(`/notifications/${notificationId}`);
  },
};
